import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocket } from 'socket.io-react-hook';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';
import { _getToken } from 'src/Config/axiosApi';
import { refreshAuthToken } from 'src/Common/context/AuthContext';
import type { MatchEventFrame } from '../types/matchEvents.types';
import { compareStreamIds } from '../utils/notificationsFeed';
import { MAX_MATCH_EVENT_ROWS } from '../utils/matchEventsFeed';

/**
 * Seconds until a JWT's `exp`, or null if it can't be decoded. Local
 * copy (AuthContext's helper is not exported) — only reads `exp`. Mirror
 * of the same helper in `useNotificationsSocket`.
 */
function secondsUntilTokenExpiry(token: string | null): number | null {
  if (!token) return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(
      atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
    ) as { exp?: number };
    if (!payload?.exp) return null;
    return payload.exp - Math.floor(Date.now() / 1000);
  } catch {
    return null;
  }
}

/** Refresh the token this many seconds BEFORE it expires. */
const PROACTIVE_REFRESH_LEAD_SECONDS = 60;

/** Cooldown between auto-refresh attempts on a connect/auth error. */
const AUTH_RECOVERY_COOLDOWN_MS = 10_000;

/** The WS namespace for the live match-events feed (frozen contract §WS). */
export const MATCH_EVENTS_NAMESPACE = '/match-events';

/**
 * Resolve the namespaced socket URL. The feed shares the notifications-
 * websocket base (`VITE_NOTIFICATIONS_WS_URL`) and lives on the
 * `/match-events` socket.io namespace, so we append the namespace path
 * to the base URL (socket.io routes the trailing path to the namespace).
 * A dedicated `VITE_MATCH_EVENTS_WS_URL` overrides the base if the feed
 * is ever split onto its own host (mirrors the `VITE_LIVE_PREDICTIONS_WS_URL`
 * fallback precedent).
 */
export function resolveMatchEventsWsUrl(): string {
  const base =
    import.meta.env.VITE_MATCH_EVENTS_WS_URL ||
    import.meta.env.VITE_NOTIFICATIONS_WS_URL ||
    '';
  if (!base) return '';
  return `${base.replace(/\/+$/, '')}${MATCH_EVENTS_NAMESPACE}`;
}

export interface UseMatchEventsSocketOptions {
  /** The fixture to subscribe to. `null` = subscribe to nothing. */
  fixtureId: number | null;
  /** Request the raw firehose (`player_stat` + `timer`) for this fixture. */
  includeRaw?: boolean;
}

export interface MatchEventsSocketApi {
  isConnected: boolean;
  /** Buffered frames for the current fixture, newest LAST (append order). */
  frames: MatchEventFrame[];
  /** Bumps on every enqueue so a consumer effect can re-run. */
  frameVersion: number;
  /** Clear the buffered frames (e.g. after switching fixture). */
  clearFrames: () => void;
  /** Id of the last applied stream entry (`<ms>-<seq>`), '' if none. */
  lastStreamId: string;
}

/**
 * Connects to the notifications-websocket service on the `/match-events`
 * namespace and streams `match_event` frames for ONE selected fixture.
 *
 * Subscription model (frozen contract §WS `/match-events`):
 *  - On every (re)connect — and whenever `fixtureId`/`includeRaw` change —
 *    we `subscribe_match { fixtureId, lastStreamId?, includeRaw? }` for the
 *    active fixture (carrying the replay cursor when we have one) and
 *    `unsubscribe_match { fixtureId }` for the previous one.
 *  - Frames arrive on `match_event`; each carries a `streamId` cursor so a
 *    reconnect replays via `XRANGE` with no gap.
 *  - Switching fixture resets the buffer + cursor (a fresh fixture starts
 *    from a clean feed).
 *
 * Auth / resilience: reuses the notifications-socket pattern VERBATIM —
 * socket.io owns an infinite, backed-off reconnect loop; we proactively
 * refresh the JWT before `exp`; on `connect_error`/`auth_error` we
 * refresh-and-retry (once per cooldown) without ever tearing the socket
 * down or logging the operator out (`clearOnFailure: false`).
 */
export function useMatchEventsSocket(
  options: UseMatchEventsSocketOptions,
): MatchEventsSocketApi {
  const { fixtureId, includeRaw = false } = options;
  const wsUrl = resolveMatchEventsWsUrl();
  const accessToken = _getToken();

  const { socket } = useSocket(wsUrl || '', {
    enabled: !!wsUrl && !!accessToken,
    transports: ['websocket'],
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 30_000,
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() }),
  });

  const [frames, setFrames] = useState<MatchEventFrame[]>([]);
  const [frameVersion, setFrameVersion] = useState(0);

  // Replay cursor — id of the most-recently-applied stream entry. Empty
  // means "never applied one" so the first subscribe omits it.
  const lastStreamIdRef = useRef<string>('');
  const [lastStreamId, setLastStreamId] = useState<string>('');

  // The currently-subscribed fixture — so a fixture change can
  // unsubscribe the OLD one before subscribing the new one.
  const subscribedFixtureRef = useRef<number | null>(null);
  const includeRawRef = useRef<boolean>(includeRaw);
  includeRawRef.current = includeRaw;

  const lastAuthRecoveryAt = useRef(0);

  const clearFrames = useCallback(() => {
    setFrames([]);
    lastStreamIdRef.current = '';
    setLastStreamId('');
  }, []);

  // ── Frame intake: buffer every `match_event`, advance the cursor ────────
  useEffect(() => {
    const onMatchEvent = (frame: MatchEventFrame) => {
      // Ignore stray frames for a fixture we're no longer viewing.
      if (
        subscribedFixtureRef.current != null &&
        frame.fixtureId !== subscribedFixtureRef.current
      ) {
        return;
      }
      const incomingId = frame.streamId;
      if (incomingId) {
        const cursor = lastStreamIdRef.current;
        const cmp = cursor ? compareStreamIds(incomingId, cursor) : 1;
        if (cmp <= 0) {
          // Already-applied stream id — a reconnect/replay re-delivery of a
          // frame we've seen. Drop it, else "show all" mode appends a
          // duplicate row and reuses its React key (streamId).
          return;
        }
        lastStreamIdRef.current = incomingId;
        setLastStreamId(incomingId);
      }
      setFrames((prev) => {
        const next = [...prev, frame];
        // Bound the buffer — drop the oldest rows past the ceiling.
        return next.length > MAX_MATCH_EVENT_ROWS
          ? next.slice(next.length - MAX_MATCH_EVENT_ROWS)
          : next;
      });
      setFrameVersion((n) => n + 1);
    };

    socket.on('match_event', onMatchEvent);
    return () => {
      socket.off('match_event', onMatchEvent);
    };
  }, [socket]);

  // ── (Re)subscribe on connect AND on fixture / includeRaw change ─────────
  useEffect(() => {
    const subscribe = () => {
      const prevFixture = subscribedFixtureRef.current;
      // A fixture change: leave the old room and reset the buffer/cursor.
      if (prevFixture != null && prevFixture !== fixtureId) {
        socket.emit('unsubscribe_match', { fixtureId: prevFixture });
        lastStreamIdRef.current = '';
        setLastStreamId('');
        setFrames([]);
      }
      subscribedFixtureRef.current = fixtureId;
      if (fixtureId == null) return;
      const cursor = lastStreamIdRef.current;
      socket.emit('subscribe_match', {
        fixtureId,
        ...(cursor ? { lastStreamId: cursor } : {}),
        includeRaw: includeRawRef.current,
      });
    };

    socket.on('connect', subscribe);
    if (socket.connected) subscribe();

    return () => {
      socket.off('connect', subscribe);
    };
  }, [socket, socket.connected, fixtureId, includeRaw]);

  // ── Leave the room on unmount ───────────────────────────────────────────
  useEffect(() => {
    return () => {
      const fx = subscribedFixtureRef.current;
      if (fx != null) {
        try {
          socket.emit('unsubscribe_match', { fixtureId: fx });
        } catch {
          /* socket already torn down */
        }
      }
    };
  }, [socket]);

  // ── Proactive token refresh before `exp` ────────────────────────────────
  useEffect(() => {
    const ttl = secondsUntilTokenExpiry(accessToken);
    if (ttl === null) return;
    const delayMs = Math.max(0, (ttl - PROACTIVE_REFRESH_LEAD_SECONDS) * 1000);
    const timer = setTimeout(() => {
      refreshAuthToken({ clearOnFailure: false }).catch((err) => {
        Sentry.captureException(err, { tags: { area: 'match-events-ws' } });
      });
    }, delayMs);
    return () => clearTimeout(timer);
  }, [accessToken]);

  // ── Auth-error recovery: refresh-and-retry, NEVER give up ───────────────
  useEffect(() => {
    const handleAuthError = async (err?: unknown) => {
      const now = Date.now();
      if (now - lastAuthRecoveryAt.current < AUTH_RECOVERY_COOLDOWN_MS) {
        Sentry.captureException(
          err instanceof Error ? err : new Error('match-events WS auth error'),
          { tags: { area: 'match-events-ws' } },
        );
        return;
      }
      lastAuthRecoveryAt.current = now;

      let newToken: string | null = null;
      try {
        newToken = await refreshAuthToken({ clearOnFailure: false });
      } catch (refreshErr) {
        Sentry.captureException(refreshErr, {
          tags: { area: 'match-events-ws' },
        });
      }

      if (newToken) {
        try {
          socket.disconnect();
        } catch {
          /* already closed */
        }
        socket.connect();
      } else {
        toast.error(
          'Live match events reconnecting — will recover automatically.',
          { toastId: 'match-events-ws-auth' },
        );
        Sentry.captureException(
          err instanceof Error
            ? err
            : new Error('match-events WS auth error — refresh failed'),
          { tags: { area: 'match-events-ws' } },
        );
      }
    };

    socket.on('connect_error', handleAuthError);
    socket.on('auth_error', handleAuthError);
    return () => {
      socket.off('connect_error', handleAuthError);
      socket.off('auth_error', handleAuthError);
    };
  }, [socket]);

  return {
    isConnected: socket.connected,
    frames,
    frameVersion,
    clearFrames,
    lastStreamId,
  };
}
